import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';

import { useAdminUsersQuery } from '@/client/api/admin/users.query';
import { useAdminUsersBulkMutation, useAdminUsersExportMutation } from '@/client/api/admin/users.mutation';
import { useRoleTypesQuery } from '@/client/api/admin/roles';
import type { AdminUserBulkAction, AdminUserBulkExtras, AdminUserStatus } from '@/client/types/admin/users-orgs';

import AddUserDialog from '@/client/components/admin/UsersAndPermissions/AddUserDialog';
import Pagination from '@/client/components/common/Pagination';
import StatsGrid from '@/client/components/common/StatsGrid';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import { Button } from '@/components/ui/button';

import { BulkActionBar } from './UserList/BulkActionBar';
import { BulkActionDialog } from './UserList/BulkActionDialog';
import { downloadCsv } from './UserList/export-csv';
import { FilterChips, type FilterChip } from './UserList/FilterChips';
import { roleTypeLabel } from './UserList/helpers';
import { buildAccountStats } from './UserList/stats';
import { CREATED_PRESETS, USER_STATUSES, useUserListState } from './UserList/use-user-list-state';
import { useUserSelection } from './UserList/use-user-selection';
import { UsersFilters } from './UserList/UsersFilters';
import { UsersTable } from './UserList/UsersTable';

const UserList = () => {
  useBreadcrumbs([{ label: 'Users Management', href: '#' }, { label: 'Users' }]);
  const navigate = useNavigate();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<{ action: AdminUserBulkAction; id: number } | null>(null);

  const state = useUserListState();
  const { data: roleTypes = [] } = useRoleTypesQuery();
  const roleTypeById = useMemo(() => new Map(roleTypes.map((rt) => [rt.uartid, rt])), [roleTypes]);

  const { data, isLoading, isFetching, error } = useAdminUsersQuery(state.params);
  const rows = useMemo(() => data?.users ?? [], [data]);
  const total = data?.total_users ?? rows.length;
  const pageUids = useMemo(() => rows.map(({ user }) => user.uid), [rows]);

  const selection = useUserSelection(JSON.stringify(state.listFilters));
  const selectedCount = selection.count(total);
  const pageFullySelected = pageUids.length > 0 && pageUids.every((uid) => selection.isSelected(uid));

  const bulk = useAdminUsersBulkMutation();
  const exporter = useAdminUsersExportMutation();

  const summary = data?.user_summary;
  const accountStats = useMemo(() => buildAccountStats(summary), [summary]);

  // A narrower filter can leave the page number past the last page.
  const { page, limit, handlePageChange } = state;
  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(1, Math.ceil(total / limit));
    if (page > lastPage) handlePageChange(lastPage);
  }, [data, total, limit, page, handlePageChange]);

  const handleStatClick = (key: string) => {
    const status = key as AdminUserStatus;
    // A tile narrows to its bucket; clicking the only active one clears it.
    state.setStatuses(state.statuses.length === 1 && state.statuses[0] === status ? [] : [status]);
  };

  const chips: FilterChip[] = [
    ...state.roleIds.map((id) => ({
      key: `role-${id}`,
      label: `Role: ${roleTypeById.get(id) ? roleTypeLabel(roleTypeById.get(id)!) : `#${id}`}`,
      onRemove: () => state.setRoles(state.roleIds.filter((r) => r !== id)),
    })),
    ...state.statuses.map((s) => ({
      key: `status-${s}`,
      label: `Status: ${USER_STATUSES.find((u) => u.key === s)?.label ?? s}`,
      onRemove: () => state.setStatuses(state.statuses.filter((x) => x !== s)),
    })),
    ...(state.email !== 'any'
      ? [{ key: 'email', label: `Email ${state.email}`, onRemove: () => state.setEmail('any') }]
      : []),
    ...(state.mfa !== 'any'
      ? [{ key: 'mfa', label: `Two-step ${state.mfa}`, onRemove: () => state.setMfa('any') }]
      : []),
    ...(state.created
      ? [{
          key: 'created',
          label:
            state.created === 'custom'
              ? `Created ${state.createdFrom || '…'} – ${state.createdTo || '…'}`
              : `Created: ${CREATED_PRESETS.find((p) => p.key === state.created)?.label ?? state.created}`,
          onRemove: () => state.setCreated(''),
        }]
      : []),
    ...(state.debouncedSearchTerm
      ? [{ key: 'search', label: `Search: “${state.debouncedSearchTerm}”`, onRemove: () => state.handleSearch('') }]
      : []),
  ];

  const runBulk = async (action: AdminUserBulkAction, extras: AdminUserBulkExtras) => {
    const result = await bulk.mutateAsync({
      ...selection.toSelection(state.listFilters, total),
      action,
      ...extras,
    });
    if (!result.dry_run && result.updated.length > 0) selection.clear();
    return result;
  };

  const handleExport = async () => {
    try {
      const out = await exporter.mutateAsync(selection.toSelection(state.listFilters, total));
      downloadCsv(out.csv, out.filename);
      toast.success(`Exported ${out.count.toLocaleString()} user${out.count === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Export failed'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users</h1>
            <p className="mt-1 text-sm text-gray-500">Manage and monitor user accounts</p>
          </div>
          <Button onClick={() => setIsAddOpen(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Add User
          </Button>
        </div>

        <StatsGrid
          stats={accountStats}
          isLoading={isLoading && !summary}
          error={error ? (error as Error).message : null}
          className="lg:grid-cols-4 xl:grid-cols-4"
          onStatClick={handleStatClick}
          activeKeys={state.statuses}
        />

        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
          <UsersFilters
            search={state.searchTerm}
            onSearch={state.handleSearch}
            searchBusy={state.isSearchPending || isFetching}
            roleTypes={roleTypes}
            roleIds={state.roleIds}
            onRoles={state.setRoles}
            statuses={state.statuses}
            onStatuses={state.setStatuses}
            email={state.email}
            onEmail={state.setEmail}
            mfa={state.mfa}
            onMfa={state.setMfa}
            created={state.created}
            createdFrom={state.createdFrom}
            createdTo={state.createdTo}
            onCreated={state.setCreated}
            hasFilters={state.hasFilters}
            onClearAll={state.resetFilters}
          />
          <FilterChips chips={chips} />
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          {selectedCount > 0 && (
            <BulkActionBar
              count={selectedCount}
              total={total}
              allMatching={selection.allMatching}
              pageFullySelected={pageFullySelected}
              onSelectAllMatching={selection.selectAllMatching}
              onClear={selection.clear}
              onAction={(action) => setBulkAction({ action, id: Date.now() })}
              onExport={handleExport}
              exporting={exporter.isPending}
            />
          )}

          <UsersTable
            rows={rows}
            loading={isLoading}
            limit={state.limit}
            sortBy={state.sortBy}
            sortOrder={state.sortOrder}
            onToggleSort={state.toggleSort}
            onRowClick={(uid) => navigate(`/admin/users/${uid}`)}
            roleTypeById={roleTypeById}
            isSelected={selection.isSelected}
            onToggleRow={(uid) => selection.toggle(uid, pageUids)}
            onTogglePage={(checked) => selection.setPage(pageUids, checked)}
          />

          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <Pagination
              total={total}
              page={state.page}
              itemsPerPage={state.limit}
              limit={state.limit}
              setPage={state.handlePageChange}
              setItemsPerPage={state.handleLimitChange}
              showButtons={true}
              showSelect={true}
              selectOptions={[10, 20, 50, 100, 200]}
            />
            {isFetching && !isLoading && <p className="text-xs text-gray-400 mt-1">Refreshing…</p>}
          </div>
        </div>
      </div>

      {bulkAction && (
        <BulkActionDialog
          key={bulkAction.id}
          action={bulkAction.action}
          count={selectedCount}
          roleTypes={roleTypes}
          onRun={(extras) => runBulk(bulkAction.action, extras)}
          onClose={() => setBulkAction(null)}
        />
      )}

      <AddUserDialog open={isAddOpen} onClose={() => setIsAddOpen(false)} />
    </div>
  );
};

export default UserList;
